import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { Globe, Palette, Clock, Rocket, Lightbulb, Check, X, Pencil, Lock, Eye, Link as LinkIcon, Clipboard } from 'lucide-react';

const ShowcaseDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ titre: '', slug: '' });
  const [creating, setCreating] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, message: '' });

  useEffect(() => {
    if (!user || (user.role !== 'author' && user.role !== 'admin')) {
      navigate('/services');
      return;
    }
    loadSites();
  }, [user, navigate]);

  const loadSites = async () => {
    try {
      const res = await api.get('/showcase/sites');
      setSites(res.data.sites || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Vérification de la disponibilité du slug
  useEffect(() => {
    const checkSlug = async () => {
      const slug = form.slug.trim();
      
      // Reset si slug vide
      if (!slug) {
        setSlugStatus({ checking: false, available: null, message: '' });
        return;
      }

      // Vérification format slug
      if (!/^[a-z0-9-]+$/.test(slug)) {
        setSlugStatus({ checking: false, available: false, message: 'Caractères invalides (a-z, 0-9, - uniquement)' });
        return;
      }

      if (slug.length < 3) {
        setSlugStatus({ checking: false, available: false, message: 'Minimum 3 caractères' });
        return;
      }

      setSlugStatus({ checking: true, available: null, message: 'Vérification...' });

      // Délai pour éviter trop de requêtes
      const timer = setTimeout(async () => {
        try {
          const res = await api.get(`/showcase/check-slug/${slug}`);
          if (res.data.available) {
            setSlugStatus({ checking: false, available: true, message: 'Disponible' });
          } else {
            setSlugStatus({ checking: false, available: false, message: 'Déjà utilisé' });
          }
        } catch (e) {
          // Si endpoint n'existe pas, vérifier localement
          const exists = sites.some(s => s.slug === slug);
          if (exists) {
            setSlugStatus({ checking: false, available: false, message: 'Déjà utilisé' });
          } else {
            setSlugStatus({ checking: false, available: true, message: 'Disponible' });
          }
        }
      }, 500);

      return () => clearTimeout(timer);
    };

    checkSlug();
  }, [form.slug, sites]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.titre || !form.slug) return;
    setCreating(true);
    try {
      const res = await api.post('/showcase/sites', form);
      let newId = res?.data?.id;
      // Fallback robuste: recharger la liste directement et chercher par slug
      if (!newId) {
        try {
          const listRes = await api.get('/showcase/sites');
          const created = (listRes?.data?.sites || []).find(s => s.slug === form.slug);
          newId = created?.id;
        } catch (e) {
          // ignore, on gère l'alerte plus bas
        }
      }
      if (newId) {
        navigate(`/showcase/${newId}/edit`);
        // Optionnel: reset du formulaire et rafraîchissement silencieux
        setForm({ titre: '', slug: '' });
        loadSites();
      } else {
        alert("Création effectuée mais l'identifiant du site est introuvable. Recharge la page, s'il te plaît.");
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleTogglePublish = async (siteId, currentPublished) => {
    try {
      await api.put(`/showcase/sites/${siteId}/publish`, { is_published: !currentPublished });
      // Mise à jour optimiste du state
      setSites(sites.map(s => s.id === siteId ? { ...s, is_published: !currentPublished ? 1 : 0 } : s));
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors du changement de publication');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-indigo-900 flex items-center gap-3"><Globe className="w-10 h-10 text-indigo-600" /> Mon Site Vitrine</h1>
              <p className="text-gray-600 mt-2">
                {sites.length === 0 
                  ? "Créez votre site vitrine professionnel en quelques clics"
                  : "Gérez votre site vitrine professionnel"
                }
              </p>
            </div>
            <Link to="/services" className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg border border-indigo-300">← Retour</Link>
          </div>
        </div>

        {/* Si aucun site: Formulaire de création */}
        {sites.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="max-w-xl mx-auto">
              <div className="text-center mb-8">
                <div className="mb-4 flex justify-center"><Palette className="w-16 h-16 text-indigo-500" /></div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Créez votre site vitrine</h2>
                <p className="text-gray-600">
                  Un site vitrine unique pour présenter votre activité, vos services et vos coordonnées.
                </p>
              </div>

              <form onSubmit={handleCreate} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre de votre site</label>
                  <input 
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none" 
                    placeholder="Ex: Mon Entreprise, Services Pro..." 
                    value={form.titre}
                    onChange={e=>setForm({...form,titre:e.target.value})} 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse URL de votre site
                    <span className="text-gray-400 font-normal ml-1">(slug)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      /site/
                    </div>
                    <input 
                      className={`w-full pl-14 pr-10 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        slugStatus.available === true ? 'border-green-400 focus:border-green-500' :
                        slugStatus.available === false ? 'border-red-400 focus:border-red-500' :
                        'border-gray-200 focus:border-indigo-500'
                      }`}
                      placeholder="mon-entreprise" 
                      value={form.slug}
                      onChange={e=>setForm({...form,slug:e.target.value.replace(/\s+/g,'-').toLowerCase()})} 
                    />
                    {/* Indicateur de statut */}
                    {form.slug && (
                      <div className={`absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 text-sm ${
                        slugStatus.checking ? 'text-gray-400' :
                        slugStatus.available === true ? 'text-green-600' :
                        slugStatus.available === false ? 'text-red-600' : ''
                      }`}>
                        {slugStatus.checking && (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin"></div>
                        )}
                        {!slugStatus.checking && slugStatus.available === true && (
                          <Check className="w-5 h-5 text-green-500" />
                        )}
                        {!slugStatus.checking && slugStatus.available === false && (
                          <X className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Lettres minuscules, chiffres et tirets uniquement</p>
                </div>
                
                {/* Message de statut du slug */}
                {form.slug && slugStatus.message && !slugStatus.checking && (
                  <div className={`text-sm px-3 py-2 rounded-lg ${
                    slugStatus.available ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {slugStatus.message}
                  </div>
                )}

                <button 
                  disabled={creating || !form.titre || !form.slug || slugStatus.available === false || slugStatus.checking} 
                  className="w-full px-6 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-semibold text-lg hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg"
                >
                  {creating ? <><Clock className="w-5 h-5 inline mr-2 animate-spin" /> Création en cours...</> : <><Rocket className="w-5 h-5 inline mr-2" /> Créer mon site vitrine</>}
                </button>
              </form>

              {/* Info */}
              <div className="mt-8 p-4 bg-indigo-50 rounded-xl">
                <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-yellow-500" /> Ce que vous pourrez faire :</h4>
                <ul className="text-sm text-indigo-800 space-y-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Personnaliser le design et les couleurs</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Ajouter vos services et tarifs</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Afficher vos coordonnées et réseaux sociaux</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Publier et partager votre site avec un lien unique</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          /* Si site existant: Affichage du site */
          <div className="space-y-4">
            {sites.map(site => (
              <div key={site.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Header gradient */}
                <div className={`h-32 bg-gradient-to-r ${site.is_published ? 'from-green-400 to-emerald-500' : 'from-yellow-400 to-orange-400'} flex items-center justify-center`}>
                  <div className="text-white text-center">
                    <Globe className="w-12 h-12 mx-auto mb-2" />
                    <span className={`text-sm px-3 py-1 rounded-full font-semibold inline-flex items-center gap-1 ${site.is_published ? 'bg-white/20' : 'bg-white/20'}`}>
                      {site.is_published ? <><Check className="w-4 h-4" /> En ligne</> : <>◯ Brouillon</>}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">{site.titre}</h2>
                    <div className="text-gray-500 mt-1">/site/{site.slug}</div>
                  </div>

                  {/* Actions principales */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <Link 
                      to={`/showcase/${site.id}/edit`} 
                      className="flex items-center justify-center gap-2 px-6 py-4 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-semibold text-lg transition shadow-lg"
                    >
                      <Pencil className="w-5 h-5" /> Modifier mon site
                    </Link>
                    <button 
                      onClick={()=>handleTogglePublish(site.id, site.is_published)} 
                      className={`flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-lg transition shadow-lg ${
                        site.is_published 
                          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {site.is_published ? <><Lock className="w-5 h-5" /> Dépublier</> : <><Rocket className="w-5 h-5" /> Publier mon site</>}
                    </button>
                  </div>

                  {/* Voir le site si publié */}
                  {site.is_published && (
                    <a 
                      href={`/site/${site.slug}`} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl hover:from-emerald-600 hover:to-green-700 text-center font-semibold text-lg transition shadow-lg"
                    >
                      <Eye className="w-5 h-5" /> Voir mon site en ligne
                    </a>
                  )}

                  {/* URL du site */}
                  {site.is_published && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-600 mb-2 flex items-center gap-2"><LinkIcon className="w-4 h-4" /> Lien de votre site :</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 bg-white border rounded-lg text-indigo-600 text-sm truncate">
                          {window.location.origin}/site/{site.slug}
                        </code>
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/site/${site.slug}`);
                            alert('Lien copié !');
                          }}
                          className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium text-sm transition flex items-center gap-1"
                        >
                          <Clipboard className="w-4 h-4" /> Copier
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowcaseDashboard;
